import React, { useState, useEffect, useRef } from 'react';
import './EmergencyContactApp.css';
import masterContactsData from '../data/masterContacts.json';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
// Tabs removed - now using unified interface
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertCircle,
  Download,
  Upload,
  Printer,
  Plus,
  X,
  Save,
  FileText,
  Car,
  CreditCard,
  MapPin,
  Phone,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  AlertTriangle,
  Users,
  Scale,
  Home,
  Zap,
  DollarSign,
  Briefcase,
  Heart,
  Church,
  Globe,
  Stethoscope,
  Cloud,
  Folder,
  Package,
  Smile,
  CheckCircle,
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { toast } from 'sonner';
import ToolLayout from '../components/ToolLayout';

// Backend URL no longer needed - using embedded data

// Category icon mapping
const CATEGORY_ICONS = {
  'Critical / Life-Threatening Emergencies': AlertTriangle,
  'Family & Friends': Users,
  'Legal / Advocacy': Scale,
  'Transportation & Auto': Car,
  'Home / Utilities': Home,
  'Financial & Identity': DollarSign,
  'Work & Security': Briefcase,
  'Support & Crisis Resources': Heart,
  'Religious / Faith-Based Support': Church,
  'Travel / Abroad': Globe,
  'Animals & Pets': Stethoscope,
  'Disaster / Survival': Cloud,
  'Identification & Records': Folder,
  'Logistics & Deliveries': Package,
  // Custom category icons
  'Personal Contacts': Users,
  'Work Emergency': Briefcase,
  Neighborhood: Home,
  'Extended Family': Users,
};

// Predefined category list for icon mapping
const PREDEFINED_CATEGORIES = [
  'Critical / Life-Threatening Emergencies',
  'Family & Friends',
  'Legal / Advocacy',
  'Transportation & Auto',
  'Home / Utilities',
  'Financial & Identity',
  'Work & Security',
  'Support & Crisis Resources',
  'Religious / Faith-Based Support',
  'Travel / Abroad',
  'Animals & Pets',
  'Disaster / Survival',
  'Identification & Records',
  'Logistics & Deliveries',
];

// Call order / hierarchical tier for "who to call first" in print and binder
const CALL_ORDER_TIERS = [
  { value: '', label: 'None' },
  { value: 'local', label: 'Local (call first)' },
  { value: 'out-of-area', label: 'Out-of-area contact' },
  { value: 'work_school', label: 'Work & school' },
];
const TIER_ORDER_FOR_PRINT = ['local', 'out-of-area', 'work_school', ''];
const TIER_LABELS = {
  local: '1. Call first (Local)',
  'out-of-area': '2. Out-of-area contact',
  work_school: '3. Work & school',
  '': '4. Other contacts',
};

// Predefined category emoji mapping
const CATEGORY_EMOJIS = {
  'Critical / Life-Threatening Emergencies': '🚨',
  'Family & Friends': '👥',
  'Legal / Advocacy': '⚖️',
  'Transportation & Auto': '🚗',
  'Home / Utilities': '🏠',
  'Financial & Identity': '💳',
  'Work & Security': '💼',
  'Support & Crisis Resources': '❤️',
  'Religious / Faith-Based Support': '⛪',
  'Travel / Abroad': '🌍',
  'Animals & Pets': '🐾',
  'Disaster / Survival': '⛈️',
  'Identification & Records': '📁',
  'Logistics & Deliveries': '📦',
};

function EmergencyContactApp() {
  const [masterContacts, setMasterContacts] = useState({});
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [contactData, setContactData] = useState({});
  const [templateAssignments, setTemplateAssignments] = useState({
    // Structure: { contactId: { master: true, travel: false, wallet: true } }
  });
  const [customContacts, setCustomContacts] = useState([]);
  const [injectedContacts, setInjectedContacts] = useState({}); // Maps contactKey to array of injected contacts
  const [contactOrder, setContactOrder] = useState([]); // Track the order of selected contacts
  const [collapsedContacts, setCollapsedContacts] = useState(new Set()); // Track which contacts are collapsed
  const [collapsedCustomContacts, setCollapsedCustomContacts] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set()); // Track which categories are collapsed
  const [categoryOrder, setCategoryOrder] = useState([]); // Track the order of categories
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCustomContactDialog, setShowCustomContactDialog] = useState(false);
  const [newCustomContact, setNewCustomContact] = useState({
    name: '',
    phone: '',
    address: '',
    category: '',
    subcategory: '',
    notes: '',
    tier: '',
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCustomCategoryDialog, setShowCustomCategoryDialog] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [newCustomCategory, setNewCustomCategory] = useState({
    name: '',
    icon: '',
  });
  const [customCategoryIcons, setCustomCategoryIcons] = useState({}); // Store custom category icons
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState({ type: '', id: '', name: '' });
  const [highlightedItems, setHighlightedItems] = useState(new Set());
  const categoryRefs = useRef({});
  const contactRefs = useRef({});
  const [printMode, setPrintMode] = useState('');
  const initialCollapsedSet = useRef(false);

  // Load master contacts on component mount
  useEffect(() => {
    fetchMasterContacts();
  }, []);

  // Default all categories to collapsed on first load
  useEffect(() => {
    const categories = Object.keys(masterContacts);
    if (categories.length === 0 || initialCollapsedSet.current) return;
    initialCollapsedSet.current = true;
    setCollapsedCategories(new Set(categories));
  }, [masterContacts]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  const getCategoryIcon = (categoryName) => {
    const IconComponent = CATEGORY_ICONS[categoryName];
    return IconComponent ? IconComponent : AlertCircle; // Fallback icon
  };

  const fetchMasterContacts = () => {
    try {
      // Use embedded data directly - no API call needed
      setMasterContacts(masterContactsData);

      // Pre-select contacts that have phone numbers (nationwide numbers)
      const preSelectedContacts = new Set();

      Object.entries(masterContactsData).forEach(([category, contacts]) => {
        contacts.forEach((contact, index) => {
          const contactKey = `${category}-${index}`;
          // Auto-select if contact has a pre-populated phone number
          if (contact.phone && contact.phone.trim() !== '') {
            preSelectedContacts.add(contactKey);
          }
        });
      });

      setSelectedContacts(preSelectedContacts);

      if (preSelectedContacts.size > 0) {
        toast.success(`Pre-selected ${preSelectedContacts.size} contacts with nationwide numbers`);
      }
    } catch (error) {
      console.error('Error loading master contacts:', error);
      toast.error('Failed to load master contacts');
    }
  };

  const handleContactDataChange = (contactKey, field, value) => {
    // Check if this is an injected contact
    if (contactKey.includes('-injected-')) {
      // Update injected contact data
      const originalKey = contactKey.split('-injected-')[0];
      const injectedKey = contactKey;

      setInjectedContacts((prev) => ({
        ...prev,
        [originalKey]:
          prev[originalKey]?.map((contact) =>
            contact.injectedKey === injectedKey ? { ...contact, [field]: value } : contact
          ) || [],
      }));
    } else {
      // Handle regular contact data
      setContactData((prev) => ({
        ...prev,
        [contactKey]: {
          ...prev[contactKey],
          [field]: value,
        },
      }));
    }
  };

  const handleContactToggle = (contactKey, checked) => {
    setSelectedContacts((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(contactKey);
        // Add to the end of the contact order if not already present
        setContactOrder((prevOrder) => {
          if (!prevOrder.includes(contactKey)) {
            return [...prevOrder, contactKey];
          }
          return prevOrder;
        });
      } else {
        newSelected.delete(contactKey);
        // Remove from contact order
        setContactOrder((prevOrder) => prevOrder.filter((id) => id !== contactKey));
      }
      return newSelected;
    });
  };

  const handleTemplateAssignment = (contactId, template, assigned) => {
    setTemplateAssignments((prev) => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        [template]: assigned,
      },
    }));
  };

  const isContactAssignedToTemplate = (contactId, template) => {
    return templateAssignments[contactId]?.[template] || false;
  };

  const isContactSelected = (contactKey) => {
    return selectedContacts.has(contactKey);
  };

  const addCustomContact = () => {
    if (!newCustomContact.name) {
      toast.error('Please fill in contact name');
      return;
    }

    const customContact = {
      ...newCustomContact,
      id: `custom-${Date.now()}`,
      is_custom: true,
    };

    setCustomContacts((prev) => [...prev, customContact]);
    // Auto-select the new custom contact
    setSelectedContacts((prev) => new Set([...prev, customContact.id]));

    const targetCategory = newCustomContact.category || 'Uncategorized';

    setNewCustomContact({
      name: '',
      phone: '',
      address: '',
      category: '',
      subcategory: '',
      notes: '',
      tier: '',
    });
    setShowCustomContactDialog(false);
    toast.success('Custom contact added successfully');

    // Focus and highlight the new contact
    focusAndHighlightContact(customContact.id, targetCategory);
  };

  const addCustomCategory = () => {
    if (!newCustomCategory.name) {
      toast.error('Please enter a category name');
      return;
    }

    const categoryName = newCustomCategory.name.trim();

    // Check if category already exists
    if (masterContacts[categoryName]) {
      toast.error('A category with this name already exists');
      return;
    }

    // Add the new category to master contacts with empty array
    setMasterContacts((prev) => ({
      ...prev,
      [categoryName]: [],
    }));

    // New custom category starts collapsed like the rest
    setCollapsedCategories((prev) => new Set([...prev, categoryName]));

    // Store the custom category icon if provided
    if (newCustomCategory.icon.trim()) {
      setCustomCategoryIcons((prev) => ({
        ...prev,
        [categoryName]: newCustomCategory.icon.trim(),
      }));
    }

    setNewCustomCategory({
      name: '',
      icon: '',
    });
    setShowCustomCategoryDialog(false);
    setShowEmojiPicker(false);
    toast.success(`Category "${categoryName}" added successfully`);

    // Focus and highlight the new category
    focusAndHighlightCategory(categoryName);
  };

  const onEmojiClick = (emojiData) => {
    setNewCustomCategory((prev) => ({
      ...prev,
      icon: emojiData.emoji,
    }));
    setShowEmojiPicker(false);
  };

  // Get category emoji (predefined or custom)
  const getCategoryEmoji = (category) => {
    // Check if it's a custom category with a custom icon
    if (customCategoryIcons[category]) {
      return customCategoryIcons[category];
    }
    // Check if it's a predefined category
    if (CATEGORY_EMOJIS[category]) {
      return CATEGORY_EMOJIS[category];
    }
    // Default fallback for categories without icons
    return '📋';
  };

  // Confirmation and deletion functions
  const confirmDeleteContact = (contactId, contactName, isCustom = false) => {
    setDeleteConfirmData({
      type: isCustom ? 'custom-contact' : 'master-contact',
      id: contactId,
      name: contactName,
    });
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteCategory = (categoryName) => {
    setDeleteConfirmData({
      type: 'category',
      id: categoryName,
      name: categoryName,
    });
    setShowDeleteConfirmDialog(true);
  };

  const executeDelete = () => {
    const { type, id, name } = deleteConfirmData;

    if (type === 'custom-contact') {
      removeCustomContact(id);
    } else if (type === 'master-contact') {
      removeMasterContact(id);
    } else if (type === 'category') {
      removeCategory(id);
    }

    setShowDeleteConfirmDialog(false);
    setDeleteConfirmData({ type: '', id: '', name: '' });
  };

  const removeMasterContact = (contactKey) => {
    // Parse the contact key to get category and index
    const [category, indexStr] = contactKey.split('-');
    const index = parseInt(indexStr);

    // Remove from master contacts
    const updatedMasterContacts = { ...masterContacts };
    if (updatedMasterContacts[category]) {
      updatedMasterContacts[category] = updatedMasterContacts[category].filter(
        (_, i) => i !== index
      );
      setMasterContacts(updatedMasterContacts);
    }

    // Clean up related state
    if (selectedContacts.has(contactKey)) {
      const newSelected = new Set(selectedContacts);
      newSelected.delete(contactKey);
      setSelectedContacts(newSelected);
    }

    // Clean up contact data
    const updatedContactData = { ...contactData };
    delete updatedContactData[contactKey];
    setContactData(updatedContactData);

    // Clean up collapsed state
    const newCollapsed = new Set(collapsedContacts);
    newCollapsed.delete(contactKey);
    setCollapsedContacts(newCollapsed);

    toast.success('Contact removed successfully');
  };

  const removeCategory = (categoryName) => {
    // Remove from master contacts
    const updatedMasterContacts = { ...masterContacts };
    delete updatedMasterContacts[categoryName];
    setMasterContacts(updatedMasterContacts);

    // Remove custom contacts in this category
    const updatedCustomContacts = customContacts.filter(
      (contact) => (contact.category || 'Uncategorized') !== categoryName
    );
    setCustomContacts(updatedCustomContacts);

    // Remove custom category icon
    const updatedIcons = { ...customCategoryIcons };
    delete updatedIcons[categoryName];
    setCustomCategoryIcons(updatedIcons);

    // Clean up category collapse state
    const newCollapsedCategories = new Set(collapsedCategories);
    newCollapsedCategories.delete(categoryName);
    setCollapsedCategories(newCollapsedCategories);

    toast.success(`Category "${categoryName}" removed successfully`);
  };

  // Focus and highlight new items
  const focusAndHighlightCategory = (categoryName) => {
    // Add to highlighted items
    setHighlightedItems((prev) => new Set([...prev, `category-${categoryName}`]));

    // Remove highlight after animation
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`category-${categoryName}`);
        return newSet;
      });
    }, 3000);

    // Scroll to category after a brief delay to ensure rendering
    setTimeout(() => {
      if (categoryRefs.current[categoryName]) {
        categoryRefs.current[categoryName].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);
  };

  const focusAndHighlightContact = (contactId, category) => {
    // Add to highlighted items
    setHighlightedItems((prev) => new Set([...prev, `contact-${contactId}`]));

    // Remove highlight after animation
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`contact-${contactId}`);
        return newSet;
      });
    }, 3000);

    // Scroll to category first, then contact
    setTimeout(() => {
      // Ensure category is expanded
      if (collapsedCategories.has(category)) {
        toggleCategoryCollapse(category);
      }

      // Scroll to category
      if (categoryRefs.current[category]) {
        categoryRefs.current[category].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      // Then scroll to specific contact after category scroll
      setTimeout(() => {
        if (contactRefs.current[contactId]) {
          contactRefs.current[contactId].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 500);
    }, 100);
  };

  // Get all contacts for a category (master + custom)
  const getAllContactsForCategory = (category) => {
    const masterContactsForCategory = masterContacts[category] || [];
    const customContactsForCategory = customContacts.filter(
      (contact) => (contact.category || 'Uncategorized') === category
    );

    // Convert custom contacts to the same format as master contacts
    const formattedCustomContacts = customContactsForCategory.map((contact) => ({
      ...contact,
      is_custom: true,
    }));

    return [...masterContactsForCategory, ...formattedCustomContacts];
  };

  // Get ordered categories including custom categories
  const getAllOrderedCategories = () => {
    // Get all categories from master contacts
    const masterCategories = Object.keys(masterContacts);

    // Get all unique categories from custom contacts
    const customContactCategories = [
      ...new Set(customContacts.map((contact) => contact.category || 'Uncategorized')),
    ];

    // Combine and deduplicate
    const allCategories = [...new Set([...masterCategories, ...customContactCategories])];

    // Keep ALL categories (including empty ones) - users can delete them manually if needed

    // Apply user ordering if it exists
    if (categoryOrder.length > 0) {
      const orderedCategories = categoryOrder.filter((cat) => allCategories.includes(cat));
      const remainingCategories = allCategories.filter((cat) => !categoryOrder.includes(cat));
      return [...orderedCategories, ...remainingCategories];
    }

    return allCategories;
  };

  const removeCustomContact = (contactId) => {
    setCustomContacts((prev) => prev.filter((c) => c.id !== contactId));
    setSelectedContacts((prev) => {
      const newSelected = new Set(prev);
      newSelected.delete(contactId);
      return newSelected;
    });
  };

  const updateCustomContact = (contactId, field, value) => {
    setCustomContacts((prev) =>
      prev.map((contact) => (contact.id === contactId ? { ...contact, [field]: value } : contact))
    );
  };

  const addAnotherContact = (category, contactTemplate, originalIndex) => {
    const originalContactKey = `${category}-${originalIndex}`;

    // Count existing injected contacts for this original contact to determine the next number
    const existingInjected = injectedContacts[originalContactKey] || [];
    const nextNumber = existingInjected.length + 2; // +2 because original is #1, first duplicate is #2

    const newContactKey = `${originalContactKey}-injected-${Date.now()}`;
    const newContact = {
      ...contactTemplate,
      name: `${contactTemplate.name} #${nextNumber}`,
      originalKey: originalContactKey,
      injectedKey: newContactKey,
      is_injected: true,
      phone: '', // Clear phone for user to fill in
      address: '', // Clear address for user to fill in
      notes: '',
      category: category,
    };

    // Add to injected contacts
    setInjectedContacts((prev) => ({
      ...prev,
      [originalContactKey]: [...(prev[originalContactKey] || []), newContact],
    }));

    // Auto-select the new contact
    setSelectedContacts((prev) => new Set([...prev, newContactKey]));
    toast.success(`Added another "${contactTemplate.name}" contact below`);
  };

  const removeInjectedContact = (originalContactKey, injectedKey) => {
    // Remove from injected contacts
    setInjectedContacts((prev) => ({
      ...prev,
      [originalContactKey]:
        prev[originalContactKey]?.filter((contact) => contact.injectedKey !== injectedKey) || [],
    }));

    // Remove from selected contacts
    setSelectedContacts((prev) => {
      const newSelected = new Set(prev);
      newSelected.delete(injectedKey);
      return newSelected;
    });

    // Remove from template assignments
    setTemplateAssignments((prev) => {
      const updated = { ...prev };
      delete updated[injectedKey];
      return updated;
    });

    // Remove from contact order
    setContactOrder((prev) => prev.filter((id) => id !== injectedKey));

    toast.success('Contact removed successfully');
  };

  // Collapse/expand handlers
  const toggleContactCollapse = (contactKey) => {
    setCollapsedContacts((prev) => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(contactKey)) {
        newCollapsed.delete(contactKey);
      } else {
        newCollapsed.add(contactKey);
      }
      return newCollapsed;
    });
  };

  const toggleCustomContactCollapse = (contactId) => {
    setCollapsedCustomContacts((prev) => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(contactId)) {
        newCollapsed.delete(contactId);
      } else {
        newCollapsed.add(contactId);
      }
      return newCollapsed;
    });
  };

  const isContactCollapsed = (contactKey) => {
    return collapsedContacts.has(contactKey);
  };

  const isCustomContactCollapsed = (contactId) => {
    return collapsedCustomContacts.has(contactId);
  };

  // Category collapse/expand handlers
  const toggleCategoryCollapse = (category) => {
    setCollapsedCategories((prev) => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(category)) {
        newCollapsed.delete(category);
      } else {
        newCollapsed.add(category);
      }
      return newCollapsed;
    });
  };

  const isCategoryCollapsed = (category) => {
    return collapsedCategories.has(category);
  };

  // Get ordered categories (respects drag-and-drop order)
  const getOrderedCategories = () => {
    const allCategories = Object.keys(masterContacts);

    if (categoryOrder.length === 0) {
      return allCategories;
    }

    // Sort based on the stored order
    const orderedCategories = [];
    const categorySet = new Set(allCategories);

    // Add categories in the specified order
    categoryOrder.forEach((category) => {
      if (categorySet.has(category)) {
        orderedCategories.push(category);
        categorySet.delete(category);
      }
    });

    // Add any remaining categories that weren't in the order (newly added)
    categorySet.forEach((category) => {
      orderedCategories.push(category);
    });

    return orderedCategories;
  };

  // Category drag and drop handlers
  const handleCategoryDragStart = (e, category, index) => {
    e.dataTransfer.setData('text/plain', category);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ category, index, type: 'category' })
    );
    e.dataTransfer.effectAllowed = 'move';

    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleCategoryDragEnd = (e) => {
    e.target.style.opacity = '1';
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach((el) => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Check if it's a contact being dragged over a category
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (dragData.type === 'contact') {
        // Add visual feedback for category drop zones when contact is being dragged
        const categoryContainer = e.target.closest('[draggable="true"]');
        if (categoryContainer && !categoryContainer.classList.contains('drag-over-category')) {
          // Remove drag-over from other categories
          document.querySelectorAll('.drag-over-category').forEach((el) => {
            el.classList.remove('drag-over-category');
          });

          // Add drag-over to current category
          categoryContainer.classList.add('drag-over-category');
        }
      }
    } catch (e) {
      // If we can't parse the drag data, that's okay - continue with normal category drag
    }
  };

  const handleCategoryDrop = (e, dropCategory, dropIndex) => {
    e.preventDefault();

    const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
    const { category: dragCategory, index: dragIndex, type, contactKey } = dragData;

    if (type === 'category') {
      // Handle category reordering
      if (dragCategory === dropCategory || dragIndex === dropIndex) return;

      const categories = getOrderedCategories();
      const newOrder = [...categories];

      // Remove the dragged item and insert it at the new position
      const draggedItem = newOrder.splice(dragIndex, 1)[0];
      newOrder.splice(dropIndex, 0, draggedItem);

      setCategoryOrder(newOrder);
      toast.success('Category order updated');
    } else if (type === 'contact') {
      // Handle contact being dropped on a category (move to end of that category)
      if (dragCategory === dropCategory) return; // Same category, no need to move

      const originalContact = masterContacts[dragCategory][dragIndex];
      const newContactData = { ...originalContact };

      // Update master contacts - add to new category and remove from old
      const updatedMasterContacts = { ...masterContacts };

      // Remove from original category
      updatedMasterContacts[dragCategory] = updatedMasterContacts[dragCategory].filter(
        (_, i) => i !== dragIndex
      );

      // Add to end of new category
      updatedMasterContacts[dropCategory] = [
        ...updatedMasterContacts[dropCategory],
        newContactData,
      ];

      // Update the master contacts state
      setMasterContacts(updatedMasterContacts);

      // Update contact data keys
      const oldContactKey = `${dragCategory}-${dragIndex}`;
      const newContactIndex = updatedMasterContacts[dropCategory].length - 1;
      const newContactKey = `${dropCategory}-${newContactIndex}`;

      // Transfer any existing contact data to the new key
      if (contactData[oldContactKey]) {
        const updatedContactData = { ...contactData };
        updatedContactData[newContactKey] = updatedContactData[oldContactKey];
        delete updatedContactData[oldContactKey];
        setContactData(updatedContactData);
      }

      // Update selected contacts if the contact was selected
      if (selectedContacts.has(oldContactKey)) {
        const updatedSelectedContacts = new Set(selectedContacts);
        updatedSelectedContacts.delete(oldContactKey);
        updatedSelectedContacts.add(newContactKey);
        setSelectedContacts(updatedSelectedContacts);
      }

      toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
    }
  };

  // Contact drag and drop handlers
  const handleContactDragStart = (e, contactKey, category, index) => {
    e.dataTransfer.setData('text/plain', contactKey);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        contactKey,
        category,
        index,
        type: 'contact',
      })
    );
    e.dataTransfer.effectAllowed = 'move';

    // Add visual feedback
    e.target.classList.add('dragging');
    e.stopPropagation(); // Prevent category drag
  };

  const handleContactDragEnd = (e) => {
    e.target.classList.remove('dragging');
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach((el) => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleContactDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation(); // Prevent category drag over

    // Add visual feedback for contact drop zones
    if (!e.target.classList.contains('drag-over')) {
      // Remove drag-over from other elements
      document.querySelectorAll('.drag-over').forEach((el) => {
        el.classList.remove('drag-over');
      });

      // Add drag-over to current target
      const contactCard = e.target.closest('[draggable="true"]');
      if (contactCard) {
        contactCard.classList.add('drag-over');
      }
    }
  };

  const handleContactDrop = (e, dropContactKey, dropCategory, dropIndex) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent category drop

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const {
        contactKey: dragContactKey,
        contactId,
        category: dragCategory,
        index: dragIndex,
        type,
      } = dragData;

      // Handle both master contacts and custom contacts
      if (
        (type !== 'contact' && type !== 'custom-contact') ||
        dragContactKey === dropContactKey ||
        contactId === dropContactKey
      )
        return;

      // Same contact, same position - no change needed
      if (dragCategory === dropCategory && dragIndex === dropIndex) return;

      // Handle custom contact drops
      if (type === 'custom-contact') {
        // Moving custom contact to different category
        const updatedContacts = customContacts.map((contact) =>
          contact.id === contactId ? { ...contact, category: dropCategory } : contact
        );

        setCustomContacts(updatedContacts);
        toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
        return;
      }

      // Handle master contact drops (original logic)
      if (type === 'contact') {
        // Same contact, same position - no change needed
        if (dragCategory === dropCategory && dragIndex === dropIndex) return;

        // Handle inter-category movement (moving contact to different category)
        if (dragCategory !== dropCategory) {
          // Create new contact data for the new category
          const originalContact = masterContacts[dragCategory][dragIndex];
          const newContactData = { ...originalContact };

          // Update master contacts - add to new category and remove from old
          const updatedMasterContacts = { ...masterContacts };

          // Remove from original category
          updatedMasterContacts[dragCategory] = updatedMasterContacts[dragCategory].filter(
            (_, i) => i !== dragIndex
          );

          // Add to new category at the drop position
          const newCategoryContacts = [...updatedMasterContacts[dropCategory]];
          newCategoryContacts.splice(dropIndex, 0, newContactData);
          updatedMasterContacts[dropCategory] = newCategoryContacts;

          // Update the master contacts state
          setMasterContacts(updatedMasterContacts);

          // Update contact data keys
          const oldContactKey = `${dragCategory}-${dragIndex}`;
          const newContactKey = `${dropCategory}-${dropIndex}`;

          // Transfer any existing contact data to the new key
          if (contactData[oldContactKey]) {
            const updatedContactData = { ...contactData };
            updatedContactData[newContactKey] = updatedContactData[oldContactKey];
            delete updatedContactData[oldContactKey];
            setContactData(updatedContactData);
          }

          // Update selected contacts if the contact was selected
          if (selectedContacts.has(oldContactKey)) {
            const updatedSelectedContacts = new Set(selectedContacts);
            updatedSelectedContacts.delete(oldContactKey);
            updatedSelectedContacts.add(newContactKey);
            setSelectedContacts(updatedSelectedContacts);
          }

          toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
        } else {
          // Handle intra-category reordering (moving contact within same category)
          const updatedMasterContacts = { ...masterContacts };
          const categoryContacts = [...updatedMasterContacts[dragCategory]];

          // Remove the dragged contact and insert it at the new position
          const draggedContact = categoryContacts.splice(dragIndex, 1)[0];
          categoryContacts.splice(dropIndex, 0, draggedContact);

          updatedMasterContacts[dragCategory] = categoryContacts;
          setMasterContacts(updatedMasterContacts);

          toast.success('Contact order updated');
        }
      }
    } catch (error) {
      console.error('Error handling contact drop:', error);
      toast.error('Error moving contact');
    }
  };

  // Updated drag and drop handlers for all contacts (not just selected)
  const handleDragStart = (e, contactKey, index) => {
    e.dataTransfer.setData('text/plain', contactKey);
    e.dataTransfer.setData('application/json', JSON.stringify({ contactKey, index }));
    e.dataTransfer.effectAllowed = 'move';

    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropContactKey, dropIndex) => {
    e.preventDefault();

    const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
    const { contactKey: dragContactKey, index: dragIndex } = dragData;

    if (dragContactKey === dropContactKey || dragIndex === dropIndex) return;

    // For now, we'll update the contact order state to remember user's preferred order
    // This will be used when rendering the contact list
    const allContactKeys = getAllContactKeys();
    const newOrder = [...allContactKeys];

    // Remove the dragged item and insert it at the new position
    const draggedItem = newOrder.splice(dragIndex, 1)[0];
    newOrder.splice(dropIndex, 0, draggedItem);

    setContactOrder(newOrder);
    toast.success('Contact order updated');
  };
  // Custom contact drag and drop handlers
  const handleCustomContactDragStart = (e, contactId, category) => {
    e.dataTransfer.setData('text/plain', contactId);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        contactId,
        category,
        type: 'custom-contact',
      })
    );
    e.dataTransfer.effectAllowed = 'move';

    // Add visual feedback
    e.target.classList.add('dragging');
    e.stopPropagation(); // Prevent category drag
  };

  const handleCustomContactDragEnd = (e) => {
    e.target.classList.remove('dragging');
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach((el) => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleCustomContactDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation(); // Prevent category drag over
  };

  const handleCustomContactDrop = (e, dropContactId, dropCategory) => {
    // Use the unified contact drop handler
    handleContactDrop(e, dropContactId, dropCategory, 0);
  };

  // Get all contact keys in the current display order
  const getAllContactKeys = () => {
    const keys = [];
    Object.entries(masterContacts).forEach(([category, contacts]) => {
      contacts.forEach((contact, index) => {
        const contactKey = `${category}-${index}`;
        keys.push(contactKey);

        // Add injected contacts for this original contact
        const injectedForThisContact = injectedContacts[contactKey] || [];
        injectedForThisContact.forEach((injectedContact) => {
          keys.push(injectedContact.injectedKey);
        });
      });
    });
    return keys;
  };

  // Get ordered contact keys (respects drag-and-drop order)
  const getOrderedContactKeys = () => {
    const allKeys = getAllContactKeys();

    if (contactOrder.length === 0) {
      return allKeys;
    }

    // Sort based on the stored order
    const orderedKeys = [];
    const keySet = new Set(allKeys);

    // Add contacts in the specified order
    contactOrder.forEach((key) => {
      if (keySet.has(key)) {
        orderedKeys.push(key);
        keySet.delete(key);
      }
    });

    // Add any remaining contacts that weren't in the order (newly added)
    keySet.forEach((key) => {
      orderedKeys.push(key);
    });

    return orderedKeys;
  };

  // Get ordered selected contacts (respects drag-and-drop order)
  const getOrderedSelectedContacts = () => {
    const contacts = getSelectedContacts();

    if (contactOrder.length === 0) {
      // If no custom order set, return default order
      return contacts;
    }

    // Sort contacts based on the stored order
    const orderedContacts = [];
    const contactMap = new Map(contacts.map((contact) => [contact.id, contact]));

    // Add contacts in the specified order
    contactOrder.forEach((id) => {
      if (contactMap.has(id)) {
        orderedContacts.push(contactMap.get(id));
        contactMap.delete(id);
      }
    });

    // Add any remaining contacts that weren't in the order (newly added)
    contactMap.forEach((contact) => {
      orderedContacts.push(contact);
    });

    return orderedContacts;
  };

  // Render a single contact (original or injected)
  const renderContact = (contactKey, index, isInjected = false, originalContact = null) => {
    let contact, data, isSelected;

    if (isInjected) {
      // This is an injected contact
      const [originalKey] = contactKey.split('-injected-');
      const injectedContact = injectedContacts[originalKey]?.find(
        (c) => c.injectedKey === contactKey
      );
      if (!injectedContact) return null;

      contact = injectedContact;
      data = {}; // Injected contacts store their data directly
      isSelected = isContactSelected(contactKey);
    } else {
      // This is an original contact
      const [category, contactIndex] = contactKey.split('-');
      const categoryContacts = masterContacts[category];
      if (!categoryContacts || !categoryContacts[contactIndex]) return null;

      contact = categoryContacts[contactIndex];
      data = contactData[contactKey] || {};
      isSelected = isContactSelected(contactKey);
    }

    const isCollapsed = isContactCollapsed(contactKey);
    const isDraggable = true; // All contacts are now draggable

    return (
      <div
        key={contactKey}
        className={`p-4 border rounded-lg transition-all ${
          isSelected
            ? 'bg-surface-muted border-primary/40 shadow-sm cursor-move'
            : 'bg-surface border-border hover:bg-surface-muted cursor-move'
        } ${isInjected ? 'ml-6 border-l-4 border-primary' : ''}`}
        draggable={isDraggable}
        onDragStart={(e) => handleDragStart(e, contactKey, index)}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, contactKey, index)}
        title="Drag to reorder this contact"
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            {/* Drag handle for all contacts */}
            <div
              className="flex flex-col opacity-40 hover:opacity-60 transition-opacity mt-1 cursor-move"
              title="Drag to reorder"
            >
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mb-0.5"></div>
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mb-0.5"></div>
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
            </div>

            <Checkbox
              id={contactKey}
              checked={isSelected}
              onCheckedChange={(checked) => handleContactToggle(contactKey, checked)}
              className="mt-1"
            />

            <div className="flex-1 min-w-0">
              <Label htmlFor={contactKey} className="text-sm font-medium cursor-pointer">
                {contact.name}
                {isSelected && !isCollapsed && (
                  <span className="ml-2 text-xs text-theme-accent font-normal">(selected)</span>
                )}
                {isSelected && isCollapsed && (
                  <span className="ml-2 text-xs text-green-600 font-normal">
                    (selected & collapsed)
                  </span>
                )}
              </Label>
              {contact.subcategory && (
                <Badge variant={isInjected ? 'secondary' : 'outline'} className="mt-1">
                  {isInjected
                    ? `Additional ${originalContact?.subcategory || contact.subcategory}`
                    : contact.subcategory}
                </Badge>
              )}
            </div>

            {/* Collapse/Expand button for selected contacts */}
            {isSelected && (
              <Button
                onClick={() => toggleContactCollapse(contactKey)}
                variant="ghost"
                size="sm"
                className="p-1 h-8 w-8 text-text-muted hover:text-text-primary"
                title={isCollapsed ? 'Expand contact details' : 'Collapse contact details'}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            )}

            {/* Remove button for injected contacts */}
            {isInjected && (
              <Button
                onClick={() => {
                  const [originalKey] = contactKey.split('-injected-');
                  removeInjectedContact(originalKey, contactKey);
                }}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                title="Remove this contact"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Contact details form - only show if selected and not collapsed */}
          {isSelected && !isCollapsed && (
            <>
              <div className="mb-3">
                <Label className="text-xs text-text-muted">Custom Name (Optional)</Label>
                <Input
                  value={isInjected ? contact.customName || '' : data.customName || ''}
                  onChange={(e) =>
                    handleContactDataChange(contactKey, 'customName', e.target.value)
                  }
                  placeholder={`e.g., "${contact.name}" or customize it`}
                  className="mt-1 h-8 text-sm"
                />
                <p className="text-xs text-text-muted mt-1">
                  Leave blank to use default name: "{contact.name}"
                </p>
              </div>

              <div className="mb-3 p-3 bg-surface-muted rounded-lg">
                <Label className="text-xs text-text-muted mb-2 block">
                  Print Templates (Select which templates should include this contact)
                </Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`${contactKey}-travel`}
                      checked={isContactAssignedToTemplate(contactKey, 'travel')}
                      onCheckedChange={(checked) =>
                        handleTemplateAssignment(contactKey, 'travel', checked)
                      }
                    />
                    <Label htmlFor={`${contactKey}-travel`} className="text-xs cursor-pointer">
                      Travel/Go-Bag
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`${contactKey}-wallet`}
                      checked={isContactAssignedToTemplate(contactKey, 'wallet')}
                      onCheckedChange={(checked) =>
                        handleTemplateAssignment(contactKey, 'wallet', checked)
                      }
                    />
                    <Label htmlFor={`${contactKey}-wallet`} className="text-xs cursor-pointer">
                      Wallet Card
                    </Label>
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Master List template includes all selected contacts automatically
                </p>
              </div>

              <div className="mb-3">
                <Label className="text-xs text-text-muted mb-1 block">Call order (for Master List print)</Label>
                <select
                  value={isInjected ? (contact.tier || '') : (data.tier || '')}
                  onChange={(e) => handleContactDataChange(contactKey, 'tier', e.target.value)}
                  className="w-full mt-1 h-8 text-sm rounded-md border border-input bg-background px-3 py-1"
                >
                  {CALL_ORDER_TIERS.map((opt) => (
                    <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-text-muted mt-0.5">Who to call first: Local, Out-of-area, or Work &amp; school</p>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-text-muted flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Phone Number
                  </Label>
                  <Input
                    value={isInjected ? contact.phone || '' : data.phone || contact.phone || ''}
                    onChange={(e) => handleContactDataChange(contactKey, 'phone', e.target.value)}
                    placeholder="Enter phone number"
                    className="mt-1 h-8 text-sm"
                  />
                </div>

                <div>
                  <Label className="text-xs text-text-muted flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Address
                  </Label>
                  <Input
                    value={
                      isInjected ? contact.address || '' : data.address || contact.address || ''
                    }
                    onChange={(e) => handleContactDataChange(contactKey, 'address', e.target.value)}
                    placeholder="Enter address"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-text-muted">Notes (Optional)</Label>
                <Textarea
                  value={isInjected ? contact.notes || '' : data.notes || ''}
                  onChange={(e) => handleContactDataChange(contactKey, 'notes', e.target.value)}
                  placeholder="Additional information or instructions"
                  className="mt-1 text-sm"
                  rows={2}
                />
              </div>

              {/* Add Another Button - only for original contacts */}
              {!isInjected && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <Button
                    onClick={() => {
                      const [category, contactIndex] = contactKey.split('-');
                      const originalContact = masterContacts[category][contactIndex];
                      addAnotherContact(category, originalContact, parseInt(contactIndex));
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full flex items-center gap-2 text-xs"
                  >
                    <Plus className="h-3 w-3" />
                    Add Another "{contact.name}"
                  </Button>
                  <p className="text-xs text-text-muted mt-1 text-center break-words">
                    Will appear directly below this contact
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleCustomContactToggle = (contactId, checked) => {
    setSelectedContacts((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(contactId);
      } else {
        newSelected.delete(contactId);
      }
      return newSelected;
    });
  };

  const getSelectedContacts = () => {
    const selectedContactsList = [];

    // Add selected master contacts
    Object.entries(masterContacts).forEach(([category, contacts]) => {
      contacts.forEach((contact, index) => {
        const contactKey = `${category}-${index}`;

        if (selectedContacts.has(contactKey)) {
          const data = contactData[contactKey] || {};
          selectedContactsList.push({
            id: contactKey,
            name: data.customName || contact.name, // Use custom name if provided
            originalName: contact.name, // Keep original for reference
            phone: data.phone || contact.phone || '',
            address: data.address || contact.address || '',
            category,
            subcategory: contact.subcategory || '',
            is_custom: false,
            notes: data.notes || '',
            tier: data.tier || '',
          });
        }

        // Add selected injected contacts for this original contact
        const injectedForThisContact = injectedContacts[contactKey] || [];
        injectedForThisContact.forEach((injectedContact) => {
          if (selectedContacts.has(injectedContact.injectedKey)) {
            selectedContactsList.push({
              id: injectedContact.injectedKey,
              name: injectedContact.customName || injectedContact.name,
              originalName: injectedContact.name,
              phone: injectedContact.phone || '',
              address: injectedContact.address || '',
              category,
              subcategory: contact.subcategory || '',
              is_custom: true,
              is_injected: true,
              notes: injectedContact.notes || '',
              tier: injectedContact.tier || '',
            });
          }
        });
      });
    });

    // Add selected custom contacts
    customContacts.forEach((contact) => {
      if (selectedContacts.has(contact.id)) {
        selectedContactsList.push(contact);
      }
    });

    return selectedContactsList;
  };

  const exportContactSheet = () => {
    const selectedContactsList = getSelectedContacts();

    if (selectedContactsList.length === 0) {
      toast.error('Please select some contacts before exporting');
      return;
    }

    const exportData = {
      user_name: userName,
      contacts: selectedContactsList,
      selected_contacts: Array.from(selectedContacts),
      contact_data: contactData,
      template_assignments: templateAssignments,
      custom_contacts: customContacts,
      custom_category_icons: customCategoryIcons,
      injected_contacts: injectedContacts,
      contact_order: contactOrder,
      category_order: categoryOrder,
      collapsed_contacts: Array.from(collapsedContacts),
      collapsed_categories: Array.from(collapsedCategories),
      collapsed_custom_contacts: Array.from(collapsedCustomContacts),
      exported_at: new Date().toISOString(),
      app_version: '1.0',
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `emergency-contacts-${userName || 'sheet'}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    toast.success('Contact sheet exported successfully');
  };

  // Reset all data to start fresh
  const resetAllData = () => {
    setSelectedContacts(new Set());
    setContactData({});
    setTemplateAssignments({
      travel: new Set(),
      wallet: new Set(),
    });
    setCustomContacts([]);
    setCustomCategoryIcons({});
    setInjectedContacts({});
    setContactOrder([]);
    setCategoryOrder([]);
    setCollapsedContacts(new Set());
    setCollapsedCategories(new Set());
    setCollapsedCustomContacts(new Set());
    setUserName('');
    setHighlightedItems(new Set());

    toast.success('All data cleared successfully');
  };

  const importContactSheet = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);

        console.log('Import data received:', importData);

        // Validate that this is a contact sheet export
        if (!importData.selected_contacts && !importData.contact_data && !importData.user_name) {
          throw new Error('Invalid contact sheet format');
        }

        // Import basic data
        if (importData.user_name) {
          setUserName(importData.user_name);
          console.log('Imported user name:', importData.user_name);
        }

        if (importData.contact_data) {
          setContactData(importData.contact_data);
          console.log(
            'Imported contact data:',
            Object.keys(importData.contact_data).length,
            'contacts'
          );
        }

        if (importData.custom_contacts) {
          setCustomContacts(importData.custom_contacts);
          console.log('Imported custom contacts:', importData.custom_contacts.length);
        }

        if (importData.template_assignments) {
          setTemplateAssignments(importData.template_assignments);
          console.log('Imported template assignments:', importData.template_assignments);
        }

        if (importData.selected_contacts) {
          const selectedSet = new Set(importData.selected_contacts);
          setSelectedContacts(selectedSet);
          console.log('Imported selected contacts:', selectedSet.size);
        }

        // Import extended state (new fields)
        if (importData.custom_category_icons) {
          setCustomCategoryIcons(importData.custom_category_icons);
          console.log(
            'Imported custom category icons:',
            Object.keys(importData.custom_category_icons).length
          );
        }

        if (importData.injected_contacts) {
          setInjectedContacts(importData.injected_contacts);
          console.log(
            'Imported injected contacts:',
            Object.keys(importData.injected_contacts).length
          );
        }

        if (importData.contact_order) {
          setContactOrder(importData.contact_order);
          console.log('Imported contact order:', importData.contact_order.length);
        }

        if (importData.category_order) {
          setCategoryOrder(importData.category_order);
          console.log('Imported category order:', importData.category_order.length);
        }

        if (importData.collapsed_contacts) {
          setCollapsedContacts(new Set(importData.collapsed_contacts));
          console.log('Imported collapsed contacts:', importData.collapsed_contacts.length);
        }

        if (importData.collapsed_categories) {
          setCollapsedCategories(new Set(importData.collapsed_categories));
          console.log('Imported collapsed categories:', importData.collapsed_categories.length);
        }

        if (importData.collapsed_custom_contacts) {
          setCollapsedCustomContacts(new Set(importData.collapsed_custom_contacts));
          console.log(
            'Imported collapsed custom contacts:',
            importData.collapsed_custom_contacts.length
          );
        }

        // Clear any highlighting
        setHighlightedItems(new Set());

        toast.success(
          `Contact sheet imported successfully! Loaded ${importData.selected_contacts ? importData.selected_contacts.length : 0} contacts.`
        );
      } catch (error) {
        console.error('Import error:', error);
        toast.error(`Import failed: ${error.message}`);
      }
    };

    reader.onerror = (error) => {
      console.error('File read error:', error);
      toast.error('Failed to read file');
    };

    reader.readAsText(file);

    // Reset file input so same file can be imported again
    event.target.value = '';
  };

  const printContactSheet = (mode) => {
    const selectedContactsList = getSelectedContacts();

    if (selectedContactsList.length === 0) {
      toast.error('Please select some contacts before printing');
      return;
    }

    console.log('Setting print mode to:', mode);
    setPrintMode(mode);

    // Force re-render and print
    setTimeout(() => {
      console.log('Print mode should be set, calling print');
      window.print();

      // Reset after printing
      setTimeout(() => {
        setPrintMode('');
      }, 1000);
    }, 200);
  };

  const getContactsByTemplate = (template) => {
    const orderedContacts = getOrderedSelectedContacts();

    switch (template) {
      case 'master':
        // Master template includes all selected contacts
        return orderedContacts;
      case 'travel':
        // Travel template only includes contacts user assigned to travel
        return orderedContacts.filter((contact) =>
          isContactAssignedToTemplate(contact.id, 'travel')
        );
      case 'wallet':
        // Wallet template only includes contacts user assigned to wallet
        return orderedContacts.filter((contact) =>
          isContactAssignedToTemplate(contact.id, 'wallet')
        );
      default:
        return orderedContacts;
    }
  };

  const getSelectedContactsCount = () => {
    return selectedContacts.size;
  };

  // Gamification helper functions
  const getContactCompletionStats = () => {
    const selectedContactsList = getSelectedContacts();
    const stats = {
      total: selectedContactsList.length,
      withPhone: 0,
      withAddress: 0,
      withBoth: 0,
      byCategory: {},
      templateAssignments: {
        travel: 0,
        wallet: 0,
      },
    };

    selectedContactsList.forEach((contact) => {
      const hasPhone = contact.phone && contact.phone.trim() !== '';
      const hasAddress = contact.address && contact.address.trim() !== '';

      if (hasPhone) stats.withPhone++;
      if (hasAddress) stats.withAddress++;
      if (hasPhone && hasAddress) stats.withBoth++;

      // Count by category
      const category = contact.category || 'Uncategorized';
      if (!stats.byCategory[category]) {
        stats.byCategory[category] = { total: 0, complete: 0 };
      }
      stats.byCategory[category].total++;
      if (hasPhone && hasAddress) {
        stats.byCategory[category].complete++;
      }

      // Count template assignments
      if (isContactAssignedToTemplate(contact.id, 'travel')) stats.templateAssignments.travel++;
      if (isContactAssignedToTemplate(contact.id, 'wallet')) stats.templateAssignments.wallet++;
    });

    return stats;
  };

  const calculatePrintReadinessScore = () => {
    const stats = getContactCompletionStats();
    if (stats.total === 0) return 0;

    // Base score from contact completion (60% weight)
    const completionScore = (stats.withBoth / stats.total) * 60;

    // Category coverage score (25% weight)
    const categoriesWithContacts = Object.keys(stats.byCategory).length;
    const maxCategories = Object.keys(masterContacts).length || 14;
    const categoryScore = (categoriesWithContacts / maxCategories) * 25;

    // Template assignment score (15% weight)
    const templateScore =
      (stats.templateAssignments.travel > 0 ? 7.5 : 0) +
      (stats.templateAssignments.wallet > 0 ? 7.5 : 0);

    return Math.round(completionScore + categoryScore + templateScore);
  };

  if (printMode) {
    const contactsForPrint = getContactsByTemplate(printMode);

    if (printMode === 'wallet') {
      // Limit contacts to fit within wallet dimensions
      const maxContacts = 12;
      const limitedContacts = contactsForPrint.slice(0, maxContacts);

      // Fill front completely first (8 contacts max on front, remainder on back)
      const maxFrontContacts = 8;
      const side1Contacts = limitedContacts.slice(0, maxFrontContacts);
      const side2Contacts = limitedContacts.slice(maxFrontContacts);

      return (
        <div className={`print-template ${printMode}-template`}>
          {/* Single Header for entire wallet card */}
          <div className="print-header">
            <h1>Emergency Contacts</h1>
            <p>
              {userName ? `${userName} • ` : ''}Generated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="wallet-content">
            {/* Side 1 (Front) - Fixed height, fills first */}
            <div className="wallet-side">
              <div className="side-label">FRONT</div>
              <div className="print-contacts">
                {side1Contacts.map((contact, index) => (
                  <div key={contact.id || index} className="print-contact">
                    <div className="contact-name">{contact.name}</div>
                    {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
                    {contact.address && <div className="contact-address">📍 {contact.address}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Side 2 (Back) - Fixed height, only gets overflow */}
            <div className="wallet-side">
              <div className="side-label">BACK</div>
              <div className="print-contacts">
                {side2Contacts.map((contact, index) => (
                  <div key={contact.id || index} className="print-contact">
                    <div className="contact-name">{contact.name}</div>
                    {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
                    {contact.address && <div className="contact-address">📍 {contact.address}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="fold-instructions">
            📋 Print → Cut along border → Fold horizontally at center line → Laminate
          </div>
        </div>
      );
    }

    // Regular templates (Master and Travel)
    // For master template, group by call-order tier first, then by category (who to call first)
    if (printMode === 'master') {
      const byTierThenCategory = {};
      TIER_ORDER_FOR_PRINT.forEach((t) => { byTierThenCategory[t] = {}; });
      contactsForPrint.forEach((contact) => {
        const tier = contact.tier || '';
        if (!byTierThenCategory[tier]) byTierThenCategory[tier] = {};
        const category = contact.category || 'Other';
        if (!byTierThenCategory[tier][category]) byTierThenCategory[tier][category] = [];
        byTierThenCategory[tier][category].push(contact);
      });

      return (
        <div className={`print-template ${printMode}-template`}>
          <div className="print-header">
            <h1>Emergency Contact Sheet - Master List</h1>
            {userName && <p>For: {userName}</p>}
            <p>Generated: {new Date().toLocaleDateString()}</p>
            <p className="print-subtitle">Complete Reference for Home Emergency Preparedness — Who to call first</p>
          </div>

          <div className="print-contacts-grouped">
            {TIER_ORDER_FOR_PRINT.map((tier) => {
              const categories = byTierThenCategory[tier] || {};
              const hasAny = Object.values(categories).some((arr) => arr.length > 0);
              if (!hasAny) return null;
              return (
                <div key={tier || 'other'} className="print-tier-section" style={{ marginBottom: '1.5rem' }}>
                  <h2 className="print-tier-title" style={{ fontSize: '1.1rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                    {TIER_LABELS[tier]}
                  </h2>
                  {Object.entries(categories).map(([category, contacts]) => (
                    <div key={category} className="print-category-section">
                      <div className="print-category-header">
                        <div className="print-category-icon">
                          {category === 'Critical / Life-Threatening Emergencies' && <span>🚨</span>}
                          {category === 'Family & Friends' && <span>👥</span>}
                          {category === 'Legal / Advocacy' && <span>⚖️</span>}
                          {category === 'Transportation & Auto' && <span>🚗</span>}
                          {category === 'Home / Utilities' && <span>🏠</span>}
                          {category === 'Financial & Identity' && <span>💳</span>}
                          {category === 'Work & Security' && <span>💼</span>}
                          {category === 'Support & Crisis Resources' && <span>❤️</span>}
                          {category === 'Religious / Faith-Based Support' && <span>⛪</span>}
                          {category === 'Travel / Abroad' && <span>🌍</span>}
                          {category === 'Animals & Pets' && <span>🐾</span>}
                          {category === 'Disaster / Survival' && <span>⛈️</span>}
                          {category === 'Identification & Records' && <span>📁</span>}
                          {category === 'Logistics & Deliveries' && <span>📦</span>}
                          {!PREDEFINED_CATEGORIES.includes(category) && <span>👤</span>}
                        </div>
                        <h3 className="print-category-title">{category}</h3>
                        <div className="print-category-count">
                          ({contacts.length} contact{contacts.length !== 1 ? 's' : ''})
                        </div>
                      </div>

                      <div className="print-contacts">
                        {contacts.map((contact, index) => (
                          <div key={contact.id || index} className="print-contact">
                            <div className="contact-name">{contact.name}</div>
                            {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
                            {contact.address && (
                              <div className="contact-address">📍 {contact.address}</div>
                            )}
                            {contact.notes && <div className="contact-notes">{contact.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Travel template (unchanged)
    return (
      <div className={`print-template ${printMode}-template`}>
        <div className="print-header">
          <h1>Emergency Contact Sheet - Travel Reference</h1>
          {userName && <p>For: {userName}</p>}
          <p>Generated: {new Date().toLocaleDateString()}</p>
          <p className="print-subtitle">Compact Reference for Travel & Go-Bag</p>
        </div>

        <div className="print-contacts">
          {contactsForPrint.map((contact, index) => (
            <div key={contact.id || index} className="print-contact">
              <div className="contact-name">{contact.name}</div>
              {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
              {contact.address && <div className="contact-address">📍 {contact.address}</div>}
              {contact.notes && <div className="contact-notes">{contact.notes}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ToolLayout
      title="Emergency Contact Sheet Generator"
      description="Create customizable emergency contact sheets with three print-ready templates: Master list for home, travel reference, and wallet-sized card."
      icon={FileText}
      guide={{
        intro: 'Build emergency contact sheets from 140+ preloaded contacts.',
        steps: [
          'Browse and search contacts by category.',
          'Add your own custom contacts.',
          'Drag and drop to set your preferred order.',
          'Choose a template (Master List, Travel, or Wallet).',
          'Print or export for your binder.',
        ],
      }}
    >
      {/* User Info & Actions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Contact Sheet Setup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="userName">Your Name (Optional)</Label>
              <Input
                id="userName"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name for the contact sheet"
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Label htmlFor="importFile" className="cursor-pointer">
                <Button className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90" asChild>
                  <span>
                    <Upload className="h-4 w-4" />
                    Import
                  </span>
                </Button>
                <Input
                  id="importFile"
                  type="file"
                  accept=".json"
                  onChange={importContactSheet}
                  className="hidden"
                />
              </Label>
              <Button
                onClick={exportContactSheet}
                className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
              <Button
                onClick={resetAllData}
                variant="outline"
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
                Clear All
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid lg:grid-cols-[1fr_minmax(280px,380px)] gap-8">
        {/* Contact Selection */}
        <div className="min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>Emergency Contact Information</CardTitle>
              <CardDescription>
                Fill in contact details for emergency situations. Only contacts with information
                will be included in your sheet. Select a contact and expand it to set call order (who to call first) for the Master List print.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Action Buttons - Top */}
              <div className="flex gap-2 mb-6">
                <Dialog open={showCustomContactDialog} onOpenChange={setShowCustomContactDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add Custom Contact
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card text-card-foreground border-border shadow-xl">
                    <DialogHeader>
                      <DialogTitle>Add Custom Contact</DialogTitle>
                      <DialogDescription>
                        Add a contact that's not in our master list
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="customName">Contact Name *</Label>
                        <Input
                          id="customName"
                          value={newCustomContact.name}
                          onChange={(e) =>
                            setNewCustomContact((prev) => ({ ...prev, name: e.target.value }))
                          }
                          placeholder="Enter contact name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customPhone">Phone Number</Label>
                        <Input
                          id="customPhone"
                          value={newCustomContact.phone}
                          onChange={(e) =>
                            setNewCustomContact((prev) => ({ ...prev, phone: e.target.value }))
                          }
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customAddress">Address</Label>
                        <Input
                          id="customAddress"
                          value={newCustomContact.address}
                          onChange={(e) =>
                            setNewCustomContact((prev) => ({ ...prev, address: e.target.value }))
                          }
                          placeholder="Enter address"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customCategory">Category</Label>
                        <div className="relative category-dropdown-container">
                          <div className="relative">
                            <Input
                              id="customCategory"
                              value={newCustomContact.category}
                              onChange={(e) =>
                                setNewCustomContact((prev) => ({
                                  ...prev,
                                  category: e.target.value,
                                }))
                              }
                              placeholder="Select existing category or enter custom"
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>

                          {showCategoryDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto text-popover-foreground">
                              <div className="p-2">
                                <div className="text-xs font-medium text-text-muted mb-2">
                                  Existing Categories:
                                </div>
                                {Object.keys(masterContacts).map((category) => (
                                  <button
                                    key={category}
                                    type="button"
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted rounded flex items-center gap-2 text-foreground"
                                    onClick={() => {
                                      setNewCustomContact((prev) => ({ ...prev, category }));
                                      setShowCategoryDropdown(false);
                                    }}
                                  >
                                    {React.createElement(getCategoryIcon(category), {
                                      className: 'h-4 w-4 text-primary',
                                    })}
                                    {category}
                                  </button>
                                ))}
                                <div className="border-t mt-2 pt-2">
                                  <div className="text-xs font-medium text-text-muted mb-2">
                                    Popular Custom Categories:
                                  </div>
                                  {[
                                    'Personal Contacts',
                                    'Work Emergency',
                                    'Neighborhood',
                                    'Extended Family',
                                  ].map((category) => (
                                    <button
                                      key={category}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted rounded text-theme-accent flex items-center gap-2"
                                      onClick={() => {
                                        setNewCustomContact((prev) => ({ ...prev, category }));
                                        setShowCategoryDropdown(false);
                                      }}
                                    >
                                      {React.createElement(getCategoryIcon(category), {
                                        className: 'h-4 w-4 text-primary',
                                      })}
                                      {category}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-text-muted mt-1">
                          Choose from existing categories or type your own
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="customTier">Call order (for Master List print)</Label>
                        <select
                          id="customTier"
                          value={newCustomContact.tier || ''}
                          onChange={(e) =>
                            setNewCustomContact((prev) => ({ ...prev, tier: e.target.value }))
                          }
                          className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          {CALL_ORDER_TIERS.map((opt) => (
                            <option key={opt.value || 'none'} value={opt.value || ''}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-text-muted mt-1">
                          Who to call first: Local, Out-of-area, or Work &amp; school
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="customNotes">Notes (Optional)</Label>
                        <Textarea
                          id="customNotes"
                          value={newCustomContact.notes}
                          onChange={(e) =>
                            setNewCustomContact((prev) => ({ ...prev, notes: e.target.value }))
                          }
                          placeholder="Additional information"
                          rows={2}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowCustomContactDialog(false)}>
                          Cancel
                        </Button>
                        <Button onClick={addCustomContact}>Add Contact</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={showCustomCategoryDialog}
                  onOpenChange={(open) => {
                    setShowCustomCategoryDialog(open);
                    if (!open) {
                      setShowEmojiPicker(false);
                      setNewCustomCategory({ name: '', icon: '' });
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1 flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add Custom Category
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card text-card-foreground border-border shadow-xl">
                    <DialogHeader>
                      <DialogTitle>Add Custom Category</DialogTitle>
                      <DialogDescription>
                        Create a new category for organizing your contacts
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="categoryName">Category Name *</Label>
                        <Input
                          id="categoryName"
                          value={newCustomCategory.name}
                          onChange={(e) =>
                            setNewCustomCategory((prev) => ({ ...prev, name: e.target.value }))
                          }
                          placeholder="Enter category name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="categoryIcon">Category Icon/Emoji</Label>
                        <div className="flex gap-2">
                          <Input
                            id="categoryIcon"
                            value={newCustomCategory.icon}
                            onChange={(e) =>
                              setNewCustomCategory((prev) => ({ ...prev, icon: e.target.value }))
                            }
                            placeholder="Enter emoji (e.g., 🏥, 💼, 👨‍⚕️)"
                            className="flex-1"
                          />
                          <div className="relative">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                              className="shrink-0"
                            >
                              <Smile className="h-4 w-4" />
                            </Button>

                            {showEmojiPicker && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setShowEmojiPicker(false)}
                                />
                                <div className="absolute top-12 right-0 z-50 shadow-lg rounded-lg border border-border bg-popover text-popover-foreground emoji-picker-wrapper">
                                  <EmojiPicker
                                    onEmojiClick={onEmojiClick}
                                    autoFocusSearch={false}
                                    theme="light"
                                    categories={[
                                      'symbols',
                                      'smileys_people',
                                      'animals_nature',
                                      'food_drink',
                                      'travel_places',
                                      'activities',
                                      'objects',
                                      'flags',
                                    ]}
                                    width={300}
                                    height={350}
                                    skinTonesDisabled
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-text-muted mt-1">
                          Type an emoji or click the smile button to choose from picker
                        </p>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowCustomCategoryDialog(false);
                            setShowEmojiPicker(false);
                            setNewCustomCategory({ name: '', icon: '' });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button onClick={addCustomCategory}>Add Category</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Unified Contact List */}
              <div className="space-y-6">
                <div className="space-y-6">
                  {getAllOrderedCategories().map((category, categoryIndex) => {
                    const contacts = getAllContactsForCategory(category);
                    const isCatCollapsed = isCategoryCollapsed(category);

                    return (
                      <div
                        key={category}
                        ref={(el) => (categoryRefs.current[category] = el)}
                        className={`border border-border rounded-lg bg-card shadow-sm hover:shadow-md transition-all duration-300 cursor-move ${
                          highlightedItems.has(`category-${category}`)
                            ? 'ring-4 ring-primary ring-opacity-75 shadow-xl bg-surface-muted category-highlight-animation'
                            : ''
                        }`}
                        draggable={true}
                        onDragStart={(e) => handleCategoryDragStart(e, category, categoryIndex)}
                        onDragEnd={handleCategoryDragEnd}
                        onDragOver={handleCategoryDragOver}
                        onDrop={(e) => handleCategoryDrop(e, category, categoryIndex)}
                        title="Drag to reorder this entire category section"
                      >
                        {/* Category Header - Clickable to collapse/expand */}
                        <div
                          className="flex items-center gap-3 p-4 bg-surface-muted rounded-t-lg border-b border-border cursor-pointer hover:bg-surface transition-colors w-full"
                          onClick={() => toggleCategoryCollapse(category)}
                        >
                          {/* Category drag handle */}
                          <div
                            className="flex flex-col opacity-40 hover:opacity-60 transition-opacity cursor-move"
                            title="Drag to reorder category"
                          >
                            <div className="w-2 h-2 bg-text-muted rounded-full mb-0.5"></div>
                            <div className="w-2 h-2 bg-text-muted rounded-full mb-0.5"></div>
                            <div className="w-2 h-2 bg-text-muted rounded-full"></div>
                          </div>

                          {/* Collapse/Expand icon */}
                          <div className="text-text-muted">
                            {isCatCollapsed ? (
                              <ChevronRight className="h-5 w-5" />
                            ) : (
                              <ChevronDown className="h-5 w-5" />
                            )}
                          </div>

                          {/* Category icon */}
                          <div className="text-xl">
                            <span>{getCategoryEmoji(category)}</span>
                          </div>

                          {/* Category title */}
                          <h3 className="text-lg font-semibold text-text-primary flex-1 min-w-0 truncate">
                            {category}
                          </h3>

                          {/* Contact count badge */}
                          <Badge variant="secondary" className="shrink-0">
                            {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                          </Badge>

                          {/* Category delete button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent category collapse
                              confirmDeleteCategory(category);
                            }}
                            className="p-1 h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                            title="Delete this category"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Category Contacts - only show if not collapsed */}
                        {!isCatCollapsed && (
                          <div className="p-4 space-y-4">
                            {contacts.map((contact, index) => {
                              // Handle both master and custom contacts
                              const isCustomContact = contact.is_custom;
                              const contactKey = isCustomContact
                                ? contact.id
                                : `${category}-${index}`;
                              const data = isCustomContact
                                ? contact
                                : contactData[contactKey] || {};
                              const isSelected = isCustomContact
                                ? selectedContacts.has(contact.id)
                                : isContactSelected(contactKey);
                              const isCollapsed = isCustomContact
                                ? isCustomContactCollapsed(contact.id)
                                : isContactCollapsed(contactKey);

                              return (
                                <React.Fragment key={index}>
                                  {/* Original Contact */}
                                  <div
                                    ref={(el) => {
                                      if (isCustomContact) {
                                        contactRefs.current[contact.id] = el;
                                      } else {
                                        contactRefs.current[contactKey] = el;
                                      }
                                    }}
                                    className={`p-4 border rounded-lg transition-all duration-300 cursor-move ${
                                      isSelected
                                        ? 'bg-surface-muted border-primary/40 shadow-sm hover:shadow-md'
                                        : 'bg-surface border-border hover:bg-surface-muted hover:shadow-sm'
                                    } ${
                                      highlightedItems.has(
                                        `contact-${isCustomContact ? contact.id : contactKey}`
                                      )
                                        ? 'ring-4 ring-primary ring-opacity-75 shadow-xl bg-surface-muted scale-105 contact-highlight-animation'
                                        : ''
                                    }`}
                                    draggable={true}
                                    onDragStart={(e) => {
                                      if (isCustomContact) {
                                        handleCustomContactDragStart(e, contact.id, category);
                                      } else {
                                        handleContactDragStart(e, contactKey, category, index);
                                      }
                                    }}
                                    onDragEnd={handleContactDragEnd}
                                    onDragOver={handleContactDragOver}
                                    onDrop={(e) => {
                                      if (isCustomContact) {
                                        handleCustomContactDrop(e, contact.id, category);
                                      } else {
                                        handleContactDrop(e, contactKey, category, index);
                                      }
                                    }}
                                    title="Drag to reorder or move to another category"
                                  >
                                    <div className="space-y-3">
                                      <div className="flex items-start gap-3">
                                        {/* Contact drag handle */}
                                        <div
                                          className="flex flex-col opacity-40 hover:opacity-60 transition-opacity mt-1 cursor-move"
                                          title="Drag to reorder or move to another category"
                                        >
                                          <div className="w-1.5 h-1.5 bg-text-muted rounded-full mb-0.5"></div>
                                          <div className="w-1.5 h-1.5 bg-text-muted rounded-full mb-0.5"></div>
                                          <div className="w-1.5 h-1.5 bg-text-muted rounded-full"></div>
                                        </div>

                                        <Checkbox
                                          id={contactKey}
                                          checked={isSelected}
                                          onCheckedChange={(checked) => {
                                            if (isCustomContact) {
                                              handleCustomContactToggle(contact.id, checked);
                                            } else {
                                              handleContactToggle(contactKey, checked);
                                            }
                                          }}
                                          className="mt-1"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <Label
                                            htmlFor={contactKey}
                                            className="text-sm font-medium cursor-pointer"
                                          >
                                            {contact.name}
                                            {isSelected && !isCollapsed && (
                                              <span className="ml-2 text-xs text-theme-accent font-normal">
                                                (selected)
                                              </span>
                                            )}
                                            {isSelected && isCollapsed && (
                                              <span className="ml-2 text-xs text-green-600 font-normal">
                                                (selected & collapsed)
                                              </span>
                                            )}
                                          </Label>
                                          {contact.subcategory && (
                                            <Badge variant="outline" className="mt-1">
                                              {contact.subcategory}
                                            </Badge>
                                          )}
                                        </div>

                                        {/* Delete button for contacts */}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            if (isCustomContact) {
                                              confirmDeleteContact(contact.id, contact.name, true);
                                            } else {
                                              confirmDeleteContact(contactKey, contact.name, false);
                                            }
                                          }}
                                          className="p-1 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                          title="Delete this contact"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>

                                        {/* Collapse/Expand button for custom contacts */}
                                        {isSelected && isCustomContact && (
                                          <button
                                            onClick={() => toggleCustomContactCollapse(contact.id)}
                                            className="p-1 h-8 w-8 text-text-muted hover:text-text-primary hover:bg-surface-muted rounded transition-colors"
                                            title={
                                              isCollapsed
                                                ? 'Expand contact details'
                                                : 'Collapse contact details'
                                            }
                                          >
                                            {isCollapsed ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronUp className="h-4 w-4" />
                                            )}
                                          </button>
                                        )}
                                        {isSelected && !isCustomContact && (
                                          <Button
                                            onClick={() => toggleContactCollapse(contactKey)}
                                            variant="ghost"
                                            size="sm"
                                            className="p-1 h-8 w-8 text-text-muted hover:text-text-primary"
                                            title={
                                              isCollapsed
                                                ? 'Expand contact details'
                                                : 'Collapse contact details'
                                            }
                                          >
                                            {isCollapsed ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronUp className="h-4 w-4" />
                                            )}
                                          </Button>
                                        )}
                                      </div>

                                      {/* Contact details form - only show if selected and not collapsed */}
                                      {isSelected && !isCollapsed && !isCustomContact && (
                                        <>
                                          <div className="mb-3">
                                            <Label className="text-xs text-text-muted">
                                              Custom Name (Optional)
                                            </Label>
                                            <Input
                                              value={data.customName || ''}
                                              onChange={(e) =>
                                                handleContactDataChange(
                                                  contactKey,
                                                  'customName',
                                                  e.target.value
                                                )
                                              }
                                              placeholder={`e.g., "${contact.name}" or customize it`}
                                              className="mt-1 h-8 text-sm"
                                            />
                                            <p className="text-xs text-text-muted mt-1">
                                              Leave blank to use default name: "{contact.name}"
                                            </p>
                                          </div>

                                          <div className="mb-3 p-3 bg-surface-muted rounded-lg">
                                            <Label className="text-xs text-text-muted mb-2 block">
                                              Print Templates (Select which templates should include
                                              this contact)
                                            </Label>
                                            <div className="flex gap-4">
                                              <div className="flex items-center space-x-2">
                                                <Checkbox
                                                  id={`${contactKey}-travel`}
                                                  checked={isContactAssignedToTemplate(
                                                    contactKey,
                                                    'travel'
                                                  )}
                                                  onCheckedChange={(checked) =>
                                                    handleTemplateAssignment(
                                                      contactKey,
                                                      'travel',
                                                      checked
                                                    )
                                                  }
                                                />
                                                <Label
                                                  htmlFor={`${contactKey}-travel`}
                                                  className="text-xs cursor-pointer"
                                                >
                                                  Travel/Go-Bag
                                                </Label>
                                              </div>
                                              <div className="flex items-center space-x-2">
                                                <Checkbox
                                                  id={`${contactKey}-wallet`}
                                                  checked={isContactAssignedToTemplate(
                                                    contactKey,
                                                    'wallet'
                                                  )}
                                                  onCheckedChange={(checked) =>
                                                    handleTemplateAssignment(
                                                      contactKey,
                                                      'wallet',
                                                      checked
                                                    )
                                                  }
                                                />
                                                <Label
                                                  htmlFor={`${contactKey}-wallet`}
                                                  className="text-xs cursor-pointer"
                                                >
                                                  Wallet Card
                                                </Label>
                                              </div>
                                            </div>
                                            <p className="text-xs text-text-muted mt-1">
                                              Master List template includes all selected contacts
                                              automatically
                                            </p>
                                          </div>

                                          <div className="mb-3">
                                            <Label className="text-xs text-text-muted mb-1 block">
                                              Call order (for Master List print)
                                            </Label>
                                            <select
                                              value={data.tier || ''}
                                              onChange={(e) =>
                                                handleContactDataChange(
                                                  contactKey,
                                                  'tier',
                                                  e.target.value
                                                )
                                              }
                                              className="w-full mt-1 h-8 text-sm rounded-md border border-input bg-background px-3 py-1"
                                            >
                                              {CALL_ORDER_TIERS.map((opt) => (
                                                <option key={opt.value || 'none'} value={opt.value || ''}>
                                                  {opt.label}
                                                </option>
                                              ))}
                                            </select>
                                            <p className="text-xs text-text-muted mt-0.5">
                                              Who to call first: Local, Out-of-area, or Work &amp; school
                                            </p>
                                          </div>

                                          <div className="grid md:grid-cols-2 gap-3">
                                            <div>
                                              <Label className="text-xs text-text-muted flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                Phone Number
                                              </Label>
                                              <Input
                                                value={data.phone || contact.phone || ''}
                                                onChange={(e) =>
                                                  handleContactDataChange(
                                                    contactKey,
                                                    'phone',
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="Enter phone number"
                                                className="mt-1 h-8 text-sm"
                                              />
                                            </div>

                                            <div>
                                              <Label className="text-xs text-text-muted flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                Address
                                              </Label>
                                              <Input
                                                value={data.address || contact.address || ''}
                                                onChange={(e) =>
                                                  handleContactDataChange(
                                                    contactKey,
                                                    'address',
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="Enter address"
                                                className="mt-1 h-8 text-sm"
                                              />
                                            </div>
                                          </div>

                                          <div>
                                            <Label className="text-xs text-text-muted">
                                              Notes (Optional)
                                            </Label>
                                            <Textarea
                                              value={data.notes || ''}
                                              onChange={(e) =>
                                                handleContactDataChange(
                                                  contactKey,
                                                  'notes',
                                                  e.target.value
                                                )
                                              }
                                              placeholder="Additional information or instructions"
                                              className="mt-1 text-sm"
                                              rows={2}
                                            />
                                          </div>

                                          {/* Add Another Button */}
                                          <div className="mt-3 pt-3 border-t border-gray-200">
                                            <Button
                                              onClick={() =>
                                                addAnotherContact(category, contact, index)
                                              }
                                              variant="outline"
                                              size="sm"
                                              className="w-full flex items-center gap-2 text-xs"
                                            >
                                              <Plus className="h-3 w-3" />
                                              Add Another "{contact.name}"
                                            </Button>
                                            <p className="text-xs text-text-muted mt-1 text-center break-words">
                                              Will appear directly below this contact
                                            </p>
                                          </div>
                                        </>
                                      )}

                                      {/* Custom contact details form - only show if selected and not collapsed */}
                                      {isSelected && !isCollapsed && isCustomContact && (
                                        <>
                                          <div className="mb-3 p-3 bg-surface-muted rounded-lg">
                                            <Label className="text-xs text-text-muted mb-2 block">
                                              Print Templates (Select which templates should include
                                              this contact)
                                            </Label>
                                            <div className="flex gap-4">
                                              <div className="flex items-center space-x-2">
                                                <Checkbox
                                                  id={`${contact.id}-travel`}
                                                  checked={isContactAssignedToTemplate(
                                                    contact.id,
                                                    'travel'
                                                  )}
                                                  onCheckedChange={(checked) =>
                                                    handleTemplateAssignment(
                                                      contact.id,
                                                      'travel',
                                                      checked
                                                    )
                                                  }
                                                />
                                                <Label
                                                  htmlFor={`${contact.id}-travel`}
                                                  className="text-xs cursor-pointer"
                                                >
                                                  Travel/Go-Bag
                                                </Label>
                                              </div>
                                              <div className="flex items-center space-x-2">
                                                <Checkbox
                                                  id={`${contact.id}-wallet`}
                                                  checked={isContactAssignedToTemplate(
                                                    contact.id,
                                                    'wallet'
                                                  )}
                                                  onCheckedChange={(checked) =>
                                                    handleTemplateAssignment(
                                                      contact.id,
                                                      'wallet',
                                                      checked
                                                    )
                                                  }
                                                />
                                                <Label
                                                  htmlFor={`${contact.id}-wallet`}
                                                  className="text-xs cursor-pointer"
                                                >
                                                  Wallet Card
                                                </Label>
                                              </div>
                                            </div>
                                          </div>

                                          <div className="mb-3">
                                            <Label className="text-xs text-text-muted mb-1 block">
                                              Call order (for Master List print)
                                            </Label>
                                            <select
                                              value={contact.tier || ''}
                                              onChange={(e) =>
                                                updateCustomContact(
                                                  contact.id,
                                                  'tier',
                                                  e.target.value
                                                )
                                              }
                                              className="w-full mt-1 h-8 text-sm rounded-md border border-input bg-background px-3 py-1"
                                            >
                                              {CALL_ORDER_TIERS.map((opt) => (
                                                <option key={opt.value || 'none'} value={opt.value || ''}>
                                                  {opt.label}
                                                </option>
                                              ))}
                                            </select>
                                            <p className="text-xs text-text-muted mt-0.5">
                                              Who to call first: Local, Out-of-area, or Work &amp; school
                                            </p>
                                          </div>

                                          <div className="grid md:grid-cols-2 gap-3">
                                            <div>
                                              <Label className="text-xs text-text-muted flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                Phone Number
                                              </Label>
                                              <Input
                                                value={contact.phone || ''}
                                                onChange={(e) =>
                                                  updateCustomContact(
                                                    contact.id,
                                                    'phone',
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="Enter phone number"
                                                className="mt-1 h-8 text-sm"
                                              />
                                            </div>

                                            <div>
                                              <Label className="text-xs text-text-muted flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                Address
                                              </Label>
                                              <Input
                                                value={contact.address || ''}
                                                onChange={(e) =>
                                                  updateCustomContact(
                                                    contact.id,
                                                    'address',
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="Enter address"
                                                className="mt-1 h-8 text-sm"
                                              />
                                            </div>
                                          </div>

                                          <div>
                                            <Label className="text-xs text-text-muted">Notes</Label>
                                            <Textarea
                                              value={contact.notes || ''}
                                              onChange={(e) =>
                                                updateCustomContact(
                                                  contact.id,
                                                  'notes',
                                                  e.target.value
                                                )
                                              }
                                              placeholder="Additional notes"
                                              className="mt-1 text-sm"
                                              rows={2}
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Render Injected Contacts for this original contact */}
                                  {injectedContacts[contactKey]?.map(
                                    (injectedContact, injectedIndex) => {
                                      const injectedKey = injectedContact.injectedKey;
                                      const injectedSelected = isContactSelected(injectedKey);
                                      const injectedCollapsed = isContactCollapsed(injectedKey);

                                      return (
                                        <div
                                          key={injectedKey}
                                          className={`ml-6 p-4 border-l-4 border-primary rounded-lg transition-all ${
                                            injectedSelected
                                              ? 'bg-surface-muted border-primary/40 shadow-sm'
                                              : 'bg-surface border-border hover:bg-surface-muted'
                                          }`}
                                        >
                                          <div className="space-y-3">
                                            <div className="flex items-start gap-3">
                                              <Checkbox
                                                id={injectedKey}
                                                checked={injectedSelected}
                                                onCheckedChange={(checked) =>
                                                  handleContactToggle(injectedKey, checked)
                                                }
                                                className="mt-1"
                                              />
                                              <div className="flex-1 min-w-0">
                                                <Label
                                                  htmlFor={injectedKey}
                                                  className="text-sm font-medium cursor-pointer"
                                                >
                                                  {injectedContact.name}
                                                  {injectedSelected && !injectedCollapsed && (
                                                    <span className="ml-2 text-xs text-theme-accent font-normal">
                                                      (selected)
                                                    </span>
                                                  )}
                                                  {injectedSelected && injectedCollapsed && (
                                                    <span className="ml-2 text-xs text-green-600 font-normal">
                                                      (selected & collapsed)
                                                    </span>
                                                  )}
                                                </Label>
                                                <Badge variant="secondary" className="mt-1 ml-2">
                                                  Additional {contact.subcategory}
                                                </Badge>
                                              </div>

                                              {/* Collapse/Expand button for selected injected contacts */}
                                              {injectedSelected && (
                                                <Button
                                                  onClick={() => toggleContactCollapse(injectedKey)}
                                                  variant="ghost"
                                                  size="sm"
                                                  className="p-1 h-8 w-8 text-text-muted hover:text-text-primary"
                                                  title={
                                                    injectedCollapsed
                                                      ? 'Expand contact details'
                                                      : 'Collapse contact details'
                                                  }
                                                >
                                                  {injectedCollapsed ? (
                                                    <ChevronDown className="h-4 w-4" />
                                                  ) : (
                                                    <ChevronUp className="h-4 w-4" />
                                                  )}
                                                </Button>
                                              )}

                                              <Button
                                                onClick={() =>
                                                  removeInjectedContact(contactKey, injectedKey)
                                                }
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                                                title="Remove this contact"
                                              >
                                                <X className="h-4 w-4" />
                                              </Button>
                                            </div>

                                            {/* Show details only if selected and not collapsed */}
                                            {injectedSelected && !injectedCollapsed && (
                                              <>
                                                <div className="mb-3">
                                                  <Label className="text-xs text-text-muted">
                                                    Custom Name (Optional)
                                                  </Label>
                                                  <Input
                                                    value={injectedContact.customName || ''}
                                                    onChange={(e) =>
                                                      handleContactDataChange(
                                                        injectedKey,
                                                        'customName',
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder={`e.g., "${injectedContact.name}" or customize it`}
                                                    className="mt-1 h-8 text-sm"
                                                  />
                                                </div>

                                                <div className="mb-3 p-3 bg-surface-muted rounded-lg">
                                                  <Label className="text-xs text-text-muted mb-2 block">
                                                    Print Templates
                                                  </Label>
                                                  <div className="flex gap-4">
                                                    <div className="flex items-center space-x-2">
                                                      <Checkbox
                                                        id={`${injectedKey}-travel`}
                                                        checked={isContactAssignedToTemplate(
                                                          injectedKey,
                                                          'travel'
                                                        )}
                                                        onCheckedChange={(checked) =>
                                                          handleTemplateAssignment(
                                                            injectedKey,
                                                            'travel',
                                                            checked
                                                          )
                                                        }
                                                      />
                                                      <Label
                                                        htmlFor={`${injectedKey}-travel`}
                                                        className="text-xs cursor-pointer"
                                                      >
                                                        Travel/Go-Bag
                                                      </Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                      <Checkbox
                                                        id={`${injectedKey}-wallet`}
                                                        checked={isContactAssignedToTemplate(
                                                          injectedKey,
                                                          'wallet'
                                                        )}
                                                        onCheckedChange={(checked) =>
                                                          handleTemplateAssignment(
                                                            injectedKey,
                                                            'wallet',
                                                            checked
                                                          )
                                                        }
                                                      />
                                                      <Label
                                                        htmlFor={`${injectedKey}-wallet`}
                                                        className="text-xs cursor-pointer"
                                                      >
                                                        Wallet Card
                                                      </Label>
                                                    </div>
                                                  </div>
                                                </div>

                                                <div className="grid md:grid-cols-2 gap-3">
                                                  <div>
                                                    <Label className="text-xs text-text-muted flex items-center gap-1">
                                                      <Phone className="h-3 w-3" />
                                                      Phone Number
                                                    </Label>
                                                    <Input
                                                      value={injectedContact.phone || ''}
                                                      onChange={(e) =>
                                                        handleContactDataChange(
                                                          injectedKey,
                                                          'phone',
                                                          e.target.value
                                                        )
                                                      }
                                                      placeholder="Enter phone number"
                                                      className="mt-1 h-8 text-sm"
                                                    />
                                                  </div>

                                                  <div>
                                                    <Label className="text-xs text-text-muted flex items-center gap-1">
                                                      <MapPin className="h-3 w-3" />
                                                      Address
                                                    </Label>
                                                    <Input
                                                      value={injectedContact.address || ''}
                                                      onChange={(e) =>
                                                        handleContactDataChange(
                                                          injectedKey,
                                                          'address',
                                                          e.target.value
                                                        )
                                                      }
                                                      placeholder="Enter address"
                                                      className="mt-1 h-8 text-sm"
                                                    />
                                                  </div>
                                                </div>

                                                <div>
                                                  <Label className="text-xs text-text-muted">
                                                    Notes (Optional)
                                                  </Label>
                                                  <Textarea
                                                    value={injectedContact.notes || ''}
                                                    onChange={(e) =>
                                                      handleContactDataChange(
                                                        injectedKey,
                                                        'notes',
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder="Additional information or instructions"
                                                    className="mt-1 text-sm"
                                                    rows={2}
                                                  />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </React.Fragment>
                              );
                            })}

                            {/* Empty category drop zone */}
                            {(!contacts || contacts.length === 0) && (
                              <div
                                className="border-2 border-dashed border-border rounded-lg p-8 text-center text-text-muted hover:border-primary hover:bg-surface-muted transition-colors bg-surface"
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                  e.target
                                    .closest('.border-dashed')
                                    .classList.add('!border-primary', '!bg-surface-muted');
                                }}
                                onDragLeave={(e) => {
                                  e.target
                                    .closest('.border-dashed')
                                    .classList.remove('!border-primary', '!bg-surface-muted');
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.target
                                    .closest('.border-dashed')
                                    .classList.remove('!border-primary', '!bg-surface-muted');

                                  try {
                                    const dragData = JSON.parse(
                                      e.dataTransfer.getData('application/json')
                                    );
                                    const {
                                      contactKey,
                                      contactId,
                                      category: dragCategory,
                                      type,
                                    } = dragData;

                                    // Handle both master and custom contacts
                                    if (type === 'custom-contact') {
                                      // Moving custom contact to empty category
                                      const updatedContacts = customContacts.map((contact) =>
                                        contact.id === contactId
                                          ? { ...contact, category: category }
                                          : contact
                                      );
                                      setCustomContacts(updatedContacts);
                                      toast.success(`Contact moved to "${category}"`);
                                    } else if (type === 'contact') {
                                      // Moving master contact to empty category
                                      const [, indexStr] = contactKey.split('-');
                                      const index = parseInt(indexStr);
                                      const originalContact = masterContacts[dragCategory][index];

                                      if (originalContact) {
                                        // Update master contacts
                                        const updatedMasterContacts = { ...masterContacts };

                                        // Remove from original category
                                        updatedMasterContacts[dragCategory] = updatedMasterContacts[
                                          dragCategory
                                        ].filter((_, i) => i !== index);

                                        // Add to empty category
                                        if (!updatedMasterContacts[category]) {
                                          updatedMasterContacts[category] = [];
                                        }
                                        updatedMasterContacts[category].push(originalContact);

                                        setMasterContacts(updatedMasterContacts);

                                        // Update contact data keys
                                        const oldContactKey = contactKey;
                                        const newContactKey = `${category}-0`; // First contact in empty category

                                        if (contactData[oldContactKey]) {
                                          const updatedContactData = { ...contactData };
                                          updatedContactData[newContactKey] =
                                            updatedContactData[oldContactKey];
                                          delete updatedContactData[oldContactKey];
                                          setContactData(updatedContactData);
                                        }

                                        // Update selected contacts
                                        if (selectedContacts.has(oldContactKey)) {
                                          const updatedSelectedContacts = new Set(selectedContacts);
                                          updatedSelectedContacts.delete(oldContactKey);
                                          updatedSelectedContacts.add(newContactKey);
                                          setSelectedContacts(updatedSelectedContacts);
                                        }

                                        toast.success(`Contact moved to "${category}"`);
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error handling drop on empty category:', error);
                                    toast.error('Error moving contact');
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <div className="text-4xl">📋</div>
                                  <p className="font-medium">Drop contacts here</p>
                                  <p className="text-sm">
                                    This category is empty - drag contacts here to add them
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons - Bottom */}
              <div className="flex gap-2 mt-6">
                <Button
                  className="flex-1 flex items-center gap-2"
                  onClick={() => setShowCustomContactDialog(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Contact
                </Button>

                <Button
                  variant="outline"
                  className="flex-1 flex items-center gap-2"
                  onClick={() => setShowCustomCategoryDialog(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Category
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Print Readiness Dashboard */}
        <div className="space-y-6 min-w-0 overflow-visible">
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2 break-words">
                <span className="break-words">Print Readiness Dashboard</span>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      calculatePrintReadinessScore() >= 80
                        ? 'default'
                        : calculatePrintReadinessScore() >= 50
                          ? 'secondary'
                          : 'destructive'
                    }
                  >
                    {calculatePrintReadinessScore()}% Ready
                  </Badge>
                </div>
              </CardTitle>
              <CardDescription className="break-words">
                Track your progress toward a complete emergency contact sheet
              </CardDescription>
            </CardHeader>
            <CardContent>
              {getSelectedContactsCount() === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No contacts selected yet</p>
                  <p className="text-sm">Select contacts to see your progress</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Print Readiness Score Ring */}
                  <div className="text-center">
                    <div className="relative inline-flex items-center justify-center">
                      <svg className="w-24 h-24 transform -rotate-90">
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                          className="text-muted"
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 40}`}
                          strokeDashoffset={`${2 * Math.PI * 40 * (1 - calculatePrintReadinessScore() / 100)}`}
                          className={
                            calculatePrintReadinessScore() >= 80
                              ? 'text-green-500'
                              : calculatePrintReadinessScore() >= 50
                                ? 'text-yellow-500'
                                : 'text-red-500'
                          }
                          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-2xl font-bold">
                            {calculatePrintReadinessScore()}%
                          </div>
                          <div className="text-xs text-text-muted">Ready</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-surface-muted border border-border p-4 rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-muted">Total Contacts</p>
                          <p className="text-2xl font-bold text-text-primary">
                            {getContactCompletionStats().total}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-primary flex-shrink-0" />
                      </div>
                    </div>

                    <div className="bg-surface-muted border border-border p-4 rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-muted">Complete Info</p>
                          <p className="text-2xl font-bold text-primary">
                            {getContactCompletionStats().withBoth}
                          </p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-primary flex-shrink-0" />
                      </div>
                    </div>
                  </div>

                  {/* Data Completion Progress */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-text-primary break-words">
                      Data Completion Progress
                    </h4>

                    {/* Phone Numbers Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm gap-2">
                        <span className="flex items-center gap-1 min-w-0">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span className="break-words">Phone Numbers</span>
                        </span>
                        <span className="text-text-muted flex-shrink-0">
                          {getContactCompletionStats().withPhone}/
                          {getContactCompletionStats().total}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                          style={{
                            width: `${
                              getContactCompletionStats().total > 0
                                ? (getContactCompletionStats().withPhone /
                                    getContactCompletionStats().total) *
                                  100
                                : 0
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Addresses Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm gap-2">
                        <span className="flex items-center gap-1 min-w-0">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="break-words">Addresses</span>
                        </span>
                        <span className="text-text-muted flex-shrink-0">
                          {getContactCompletionStats().withAddress}/
                          {getContactCompletionStats().total}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                          style={{
                            width: `${
                              getContactCompletionStats().total > 0
                                ? (getContactCompletionStats().withAddress /
                                    getContactCompletionStats().total) *
                                  100
                                : 0
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Category Coverage */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-text-primary break-words">
                      Category Coverage
                    </h4>
                    <div className="grid gap-2">
                      {Object.entries(getContactCompletionStats().byCategory).map(
                        ([category, data]) => (
                          <div
                            key={category}
                            className="flex items-center justify-between gap-2 p-2 bg-surface-muted rounded min-w-0"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-lg flex-shrink-0">
                                {getCategoryEmoji(category)}
                              </span>
                              <span
                                className="text-sm font-medium break-words truncate"
                                title={category}
                              >
                                {category}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-text-muted">
                                {data.complete}/{data.total}
                              </span>
                              <div className="w-12 bg-muted rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all duration-300 ${
                                    data.complete === data.total
                                      ? 'bg-green-500'
                                      : data.complete > 0
                                        ? 'bg-yellow-500'
                                        : 'bg-red-500'
                                  }`}
                                  style={{ width: `${(data.complete / data.total) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Template Assignments */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-text-primary break-words">
                      Template Assignments
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between gap-2 p-2 bg-surface-muted rounded min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <Car className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm break-words">Travel</span>
                        </div>
                        <Badge
                          variant={
                            getContactCompletionStats().templateAssignments.travel > 0
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {getContactCompletionStats().templateAssignments.travel}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between gap-2 p-2 bg-surface-muted rounded min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <CreditCard className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm break-words">Wallet</span>
                        </div>
                        <Badge
                          variant={
                            getContactCompletionStats().templateAssignments.wallet > 0
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {getContactCompletionStats().templateAssignments.wallet}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Achievement Badges - theme palette (primary = green) */}
                  {(getContactCompletionStats().total >= 10 ||
                    Object.keys(getContactCompletionStats().byCategory).length >= 5 ||
                    calculatePrintReadinessScore() >= 80) && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-text-primary">Achievements</h4>
                      <div className="flex flex-wrap gap-2">
                        {getContactCompletionStats().total >= 10 && (
                          <Badge
                            variant="outline"
                            className="bg-primary/10 text-primary border-primary/30"
                          >
                            🏆 Contact Collector (10+ contacts)
                          </Badge>
                        )}
                        {Object.keys(getContactCompletionStats().byCategory).length >= 5 && (
                          <Badge
                            variant="outline"
                            className="bg-primary/10 text-primary border-primary/30"
                          >
                            🗂️ Category Master (5+ categories)
                          </Badge>
                        )}
                        {calculatePrintReadinessScore() >= 80 && (
                          <Badge
                            variant="outline"
                            className="bg-primary/10 text-primary border-primary/30"
                          >
                            ✅ Print Ready (80%+ complete)
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Print Templates */}
          {getSelectedContactsCount() > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 break-words">
                  <Printer className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words">Print Templates</span>
                </CardTitle>
                <CardDescription className="break-words">
                  Choose your preferred format for printing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={() => printContactSheet('master')}
                  className="w-full justify-between"
                  variant="outline"
                >
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Master List (Home Binder)
                  </div>
                  <Badge variant="secondary">
                    {getContactsByTemplate('master').length} contacts
                  </Badge>
                </Button>

                <Button
                  onClick={() => printContactSheet('travel')}
                  className="w-full justify-between"
                  variant="outline"
                >
                  <div className="flex items-center">
                    <Car className="h-4 w-4 mr-2" />
                    Travel/Go-Bag Reference
                  </div>
                  <Badge variant="secondary">
                    {getContactsByTemplate('travel').length} contacts
                  </Badge>
                </Button>

                <Button
                  onClick={() => printContactSheet('wallet')}
                  className="w-full justify-between"
                  variant="outline"
                >
                  <div className="flex items-center">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Wallet Card (Double-Sided)
                  </div>
                  <Badge variant="secondary">
                    {getContactsByTemplate('wallet').length} contacts
                  </Badge>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent className="bg-card text-card-foreground border-border shadow-xl">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              {deleteConfirmData.type === 'category' ? (
                <>
                  Are you sure you want to delete the category "{deleteConfirmData.name}"?
                  <br />
                  <strong>
                    This will also delete all contacts in this category and cannot be undone.
                  </strong>
                </>
              ) : (
                <>
                  Are you sure you want to delete the contact "{deleteConfirmData.name}"?
                  <br />
                  <strong>This action cannot be undone.</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirmDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={executeDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ToolLayout>
  );
}

export default EmergencyContactApp;
